/**
 * Лента сообщений внутри треда.
 *
 * Отличается от комнатной Timeline тем, что:
 *  1. Рисует КОРНЕВОЕ сообщение отдельным блоком сверху (отступ, кайма);
 *  2. Не группирует ответы по автору (в треде обычно 2-5 сообщений,
 *     группировка визуально ломает диалог);
 *  3. Не имеет своего лайтбокса: вложения в тредах пока за фичефлагом.
 *
 * ПОЧЕМУ НЕ ПЕРЕИСПОЛЬЗУЕМ Timeline.tsx: тот компонент жёстко завязан на
 * roomId (смена комнаты сбрасывает скролл), на подгрузку истории комнаты
 * и на автоскролл к низу при новом сообщении. В треде всё это то же самое,
 * но с threadId вместо roomId. Копия дешевле, чем параметризация
 * «комната или тред» в каждом useEffect.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import type { MatrixClient, Room, Thread } from "matrix-js-sdk";

import { canRedactEvent } from "../matrix/permissions";
import type { ReactionChip, TimelineItem } from "../matrix/timeline";
import { appPermalink } from "./router";
import { canShareText, shareText } from "./share";
import { palette, s } from "./styles";
import { color, font, radius, space } from "./theme";
import { MessageBody } from "./Timeline";

function timeOf(ts: number): string {
  return new Date(ts).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function dayOf(ts: number): string {
  return new Date(ts).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function permalinkOf(roomId: string, eventId: string): string {
  return appPermalink(roomId, eventId);
}

export function ThreadTimeline({
  client,
  room,
  rootId,
  thread,
  rootItem,
  items,
  reactions,
  canLoadOlder,
  loadingOlder,
  onLoadOlder,
  onReply,
  onEdit,
  onDelete,
  onReactionToggle,
}: {
  client: MatrixClient;
  room: Room;
  /** id корня треда: нужен только чтобы сбросить скролл при смене треда. */
  rootId: string;
  /** Объект треда или null, если тред ещё не создан. */
  thread: Thread | null;
  /** Корневое сообщение — рисуем отдельным блоком сверху. */
  rootItem: TimelineItem | null;
  items: TimelineItem[];
  reactions: Map<string, ReactionChip[]>;
  canLoadOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  onReply?: (eventId: string) => void;
  onEdit?: (eventId: string, currentBody: string) => void;
  onDelete?: (eventId: string) => void;
  onReactionToggle?: (eventId: string, emoji: string) => void;
}) {
  const t = useT();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);
  const heightBefore = useRef<number | null>(null);
  const lastCount = useRef(0);
  const [menu, setMenu] = useState<{ eventId: string; x: number; y: number } | null>(null);

  // Смена треда: всегда вниз, состояние прилипания сбрасываем.
  useEffect(() => {
    stickToBottom.current = true;
    heightBefore.current = null;
    lastCount.current = 0;
    const el = boxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [rootId]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    if (heightBefore.current !== null) {
      const delta = el.scrollHeight - heightBefore.current;
      heightBefore.current = null;
      if (delta > 0) el.scrollTop += delta;
    } else if (stickToBottom.current && items.length !== lastCount.current) {
      el.scrollTop = el.scrollHeight;
    }
    lastCount.current = items.length;
  }, [items]);

  const onScroll = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottom.current = atBottom;

    if (el.scrollTop < 200 && canLoadOlder && !loadingOlder) {
      heightBefore.current = el.scrollHeight;
      onLoadOlder();
    }
  }, [canLoadOlder, loadingOlder, onLoadOlder]);

  let prevDay = "";

  const renderMessage = (m: TimelineItem, isRoot = false) => {
    const day = dayOf(m.ts);
    const newDay = day !== prevDay;
    prevDay = day;
    const chips = reactions.get(m.eventId) ?? [];

    if (m.system) {
      return (
        <div key={m.eventId}>
          {newDay && (
            <div
              style={{
                textAlign: "center",
                margin: "16px 0 4px",
                fontSize: 11,
                color: palette.textDim,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {day}
            </div>
          )}
          <div
            style={{
              textAlign: "center",
              margin: "6px 0",
              fontSize: font.small,
              color: palette.textDim,
            }}
            data-testid="thread-system-event"
            data-system-kind={m.system.kind}
            title={timeOf(m.ts)}
          >
            {m.system.text}
          </div>
        </div>
      );
    }

    return (
      <div key={m.eventId}>
        {newDay && (
          <div
            style={{
              textAlign: "center",
              margin: "16px 0 4px",
              fontSize: 11,
              color: palette.textDim,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {day}
          </div>
        )}
        <div
          style={{
            ...s.msgBody,
            // Надгробие удалённого сообщения — курсив и приглушённый цвет,
            // как и в главной ленте. Пузырей в треде нет (строки, а не баллоны),
            // поэтому класс .alg-bubble.tombstone здесь неприменим, и отличие
            // выражается теми же двумя признаками напрямую.
            fontStyle: m.emote || m.redacted ? "italic" : "normal",
            opacity: m.pending ? 0.55 : 1,
            color: m.redacted ? palette.textDim : m.failed ? palette.danger : undefined,
            ...(m.mentionsMe
              ? {
                  background: color.warnBg,
                  borderLeft: `3px solid ${color.badgeHighlight}`,
                  paddingLeft: space.md,
                  borderRadius: radius.sm,
                }
              : {}),
            // Корневое сообщение визуально отличаем: светлый фон, кайма.
            // Без этого корень сливается с ответами, и непонятно, к чему
            // относится тред.
            ...(isRoot
              ? {
                  background: color.sidebarBg,
                  borderLeft: `3px solid ${color.border}`,
                  padding: space.md,
                  borderRadius: radius.sm,
                  marginBottom: space.md,
                }
              : {}),
          }}
          data-testid={isRoot ? "thread-root-message" : "thread-message"}
          data-event-id={m.eventId}
          data-mentions-me={m.mentionsMe ? "1" : "0"}
          data-redacted={m.redacted ? "1" : "0"}
          data-sender-id={m.senderId}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ eventId: m.eventId, x: e.clientX, y: e.clientY });
          }}
        >
          <div style={{ fontSize: font.small, fontWeight: font.weightBold, marginBottom: 2 }}>
            {m.senderName}
            <span style={{ fontWeight: 400, color: palette.textDim, marginLeft: 8 }}>
              {timeOf(m.ts)}
            </span>
          </div>
          {/* Надгробие — первой веткой: у redacted события тела нет,
              фраза берётся через t() для живой смены языка. */}
          {m.redacted ? (
            <span data-testid="thread-msg-tombstone">{t("message.wasDeleted")}</span>
          ) : m.emote ? (
            <>* {m.senderName} <MessageBody body={m.body} formattedBody={m.formattedBody} mentions={m.mentions} /></>
          ) : (
            <MessageBody body={m.body} formattedBody={m.formattedBody} mentions={m.mentions} />
          )}
          {m.edited && (
            <span style={{ fontSize: 11, color: palette.textDim }}>{t("common.messageEdited")}</span>
          )}
          {m.pending && <span style={{ fontSize: 11, color: palette.textDim }}>{t("common.messageSending")}</span>}
          {m.failed && <span style={{ fontSize: 11 }}>{t("common.messageNotSent")}</span>}
        </div>
        {chips.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: space.sm,
              marginTop: space.sm,
              marginBottom: space.md,
            }}
            data-testid="thread-reactions"
          >
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                style={{
                  font: "inherit",
                  fontSize: font.small,
                  padding: "2px 6px",
                  borderRadius: radius.pill,
                  border: `1px solid ${chip.mine ? color.accent : color.border}`,
                  background: chip.mine ? color.accentSoft : color.bg,
                  color: chip.mine ? color.accent : color.text,
                  cursor: "pointer",
                }}
                onClick={() => onReactionToggle?.(m.eventId, chip.key)}
                data-testid="thread-reaction-chip"
                data-reaction-key={chip.key}
                data-mine={chip.mine ? "1" : "0"}
              >
                {chip.key} {chip.count}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{ flex: "1 1 auto", overflowY: "auto", padding: "12px 16px", minHeight: 0 }}
      ref={boxRef}
      onScroll={onScroll}
      data-testid="thread-timeline"
    >
      {canLoadOlder && (
        <div style={{ textAlign: "center", padding: 8, fontSize: 12, color: palette.textDim }}>
          {loadingOlder ? t("room.historyLoading") : t("room.loadOlder")}
        </div>
      )}
      {!canLoadOlder && items.length > 0 && (
        <div style={{ textAlign: "center", padding: 8, fontSize: 12, color: palette.textDim }}>{t("thread.start")}</div>
      )}

      {/* Корневое сообщение — всегда сверху, отдельным блоком. */}
      {rootItem && renderMessage(rootItem, true)}

      {items.length === 0 && !rootItem && (
        <div style={{ ...s.centered, height: "auto", padding: 40 }}>{t("call.captionRepliesEmpty")}</div>
      )}

      {items.map((m) => renderMessage(m, false))}

      {menu && (
        <ThreadMessageMenu
          client={client}
          room={room}
          thread={thread}
          target={menu}
          items={rootItem ? [rootItem, ...items] : items}
          onClose={() => setMenu(null)}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

/** Контекстное меню сообщения в треде (правый клик). */
function ThreadMessageMenu({
  client,
  room,
  thread,
  target,
  items,
  onClose,
  onReply,
  onEdit,
  onDelete,
}: {
  client: MatrixClient;
  room: Room;
  /** Тред может ещё не существовать (новый тред) — ищем только в комнате. */
  thread: Thread | null;
  target: { eventId: string; x: number; y: number };
  items: TimelineItem[];
  onClose: () => void;
  onReply?: (eventId: string) => void;
  onEdit?: (eventId: string, currentBody: string) => void;
  onDelete?: (eventId: string) => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: target.x, top: target.y });
  const item = items.find((i) => i.eventId === target.eventId);
  const me = client.getUserId() ?? "";

  // Ищем исходное событие: сначала в тредовом таймлайне, потом в комнатном
  // (корень живёт в комнатном).
  const sourceEvent =
    thread?.liveTimeline.getEvents().find((ev) => ev.getId() === target.eventId) ??
    room.getLiveTimeline().getEvents().find((ev) => ev.getId() === target.eventId);

  const isMine = item?.senderId === me;
  const canEdit = isMine && !item?.failed && !item?.pending;
  const canDelete = sourceEvent ? canRedactEvent(client, room, sourceEvent) : false;
  const canCopy = Boolean(item?.body && !item.attachment);
  const [canShare] = useState<boolean>(() => canShareText());
  const canShareLink = Boolean(
    item && !item.pending && !item.failed && target.eventId.startsWith("$"),
  );

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const id = setTimeout(() => {
      window.addEventListener("mousedown", close);
      window.addEventListener("contextmenu", close);
      window.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("mousedown", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(target.x, window.innerWidth - r.width - 8);
    const top = Math.min(target.y, window.innerHeight - r.height - 8);
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [target.x, target.y]);

  const menuItem = (label: string, testId: string, fn: () => void, danger = false, disabled = false) => (
    <button
      key={testId}
      type="button"
      style={{
        ...s.menuItem,
        color: danger ? color.danger : color.text,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
      data-testid={testId}
      disabled={disabled}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => {
        if (disabled) return;
        fn();
        onClose();
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      ref={ref}
      style={{ ...s.menu, left: pos.left, top: pos.top }}
      data-testid="thread-message-menu"
      data-event-id={target.eventId}
      onContextMenu={(e) => e.preventDefault()}
    >
      {onReply && menuItem(t("timeline.reply"), "thread-msg-reply", () => onReply(target.eventId))}
      {onEdit && menuItem(t("timeline.edit"), "thread-msg-edit", () => {
        if (item) onEdit(target.eventId, item.body);
      }, false, !canEdit)}
      {canShare && canShareLink && menuItem(t("media.share"), "thread-msg-share", () => {
        const text = item?.body ? `${item.body}\n` : "";
        const body = `${text}${permalinkOf(room.roomId, target.eventId)}`;
        void shareText({ text: body }).then((outcome) => {
          // Запасной путь (Антон 05.10): мост и Web Share недоступны —
          // копируем ссылку в буфер, молчаливого отказа быть не должно.
          if (outcome === "unavailable") {
            void navigator.clipboard.writeText(permalinkOf(room.roomId, target.eventId));
          }
        });
      })}
      {canShareLink && menuItem(t("timeline.copyLink"), "thread-msg-copy-link", () => {
        void navigator.clipboard.writeText(permalinkOf(room.roomId, target.eventId));
      })}
      {canCopy && menuItem(t("ui.kopirovat_tekst"), "thread-msg-copy", () => {
        if (item) void navigator.clipboard.writeText(item.body);
      })}
      {onDelete && menuItem(t("common.remove"), "thread-msg-delete", () => onDelete(target.eventId), true, !canDelete)}
    </div>
  );
}
